/**
 * The audio uplink: one recording's audio, from this device to the gateway.
 *
 * This is the production transport, not an experiment. It mints the ticket, opens the
 * ticketed WebSocket, runs the 100 ms flush loop that frames canonical PCM16 packets,
 * tracks the sample cursor across a reconnect, and classifies closes. Audio reaches the
 * server by this path or not at all: PR #242 and the C7.5 chain retired the browser
 * upload routes and the batch fallback, so there is nothing beside it to compare
 * against (`docs/DEPLOY-RAILWAY.md` §6.3).
 *
 * [V] It was called `StreamProbe` until #330, from M0 when it *was* a measurement seam
 * running next to the batch upload path. The name outlived the experiment by one whole
 * migration and told every new reader the opposite of the truth.
 *
 * What is still genuinely a probe keeps the word: `streamProbeDebugEnabled` below reads
 * `?stream-probe=1` and `streamProbeManualMode` reads `?probe_manual=1`, both diagnostic
 * toggles, and both named after query flags that are frozen URL contracts.
 */
import * as api from "../../lib/api.js";
import * as K from "../../lib/const.js";
import { canonicalFloat32ToPcm16 } from "../../lib/pcm16.js";

/** The gateway's M0 maximum: 100 ms at the canonical 16 kHz rate. */
export const STREAM_FRAME_SAMPLES = 1_600;
// [V] `MIN_STREAM_FRAME_SAMPLES` (800, 50 ms) lived here: AssemblyAI rejected realtime
// packets shorter than that, so a short first frame was held and a short final tail
// dropped. The gateway opens no provider stream any more (#356) and takes any frame of
// one sample or more, so every sample is sent -- including the tail that rule threw away.
export const MAX_SOCKET_BUFFERED_BYTES = 1_024 * 1_024;
/**
 * How long `stop()` keeps sending a backlog before it closes anyway.
 *
 * [D] 90 s, inside the server's 120 s stop-barrier deadline with room for the close and
 * the archive drain after it. Audio is no longer only useful live: a device that fell
 * behind, or reconnected just before Stop, still owes the server everything it recorded.
 */
export const STOP_DRAIN_DEADLINE_MS = 90_000;
const STOP_DRAIN_POLL_MS = 50;

const SOCKET_OPEN = 1;
const SOCKET_CLOSING = 2;
const SOCKET_CLOSED = 3;
const CLOSE_WAIT_MS = 3_000;

export interface StreamFrameMetadata {
  type: "audio";
  timeline_epoch: string;
  transport_epoch: string;
  sequence: number;
  sample_offset: number;
  sample_count: number;
  sample_rate_hz: number;
  kind: "audio";
  byte_length: number;
}

export interface AudioUplinkMessage {
  type?: string;
  event?: string;
  reason?: string;
  sequence?: number;
  sample_offset?: number;
  next_sequence?: number;
  next_sample_offset?: number;
  [key: string]: unknown;
}

/** The part of Recorder the uplink reads; ownership stays with the session engine. */
export interface AudioUplinkCapture {
  /** Canonical sample count; keeping this integer avoids seconds/sample round-trips. */
  readonly sampleCount: number;
  readonly seconds: number;
  sliceSamples(fromSample: number, toSample: number): Float32Array;
  slice(fromSec: number, toSec: number): Float32Array;
}

interface UplinkSocket {
  readonly readyState: number;
  readonly bufferedAmount: number;
  binaryType: string;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  send(data: string | ArrayBuffer): void;
  close(code?: number, reason?: string): void;
}

/** Bounded context for diagnosing a browser-side transport failure. */
export interface AudioUplinkSocketDiagnostics {
  event: "error" | "close";
  closeCode: number | null;
  closeReason: string | null;
  readyState: number;
  bufferedAmount: number;
  browserOnline: boolean | null;
  visibilityState: string | null;
  wasClean: boolean;
}

/**
 * Typed classification of a WebSocket close event for bounded client recovery.
 * - "clean": normal closure (1000 or 1001) with wasClean=true; never reconnects.
 * - "resumable": a 1008 whose reason the gateway keeps the resume cursor for
 *   (`K.STREAM_RESUMABLE_CLOSE_REASONS`): the per-connection cap, a missing tail, a slow
 *   frame. Reconnect at once and carry on from the server's durable boundary (#410).
 * - "terminal_refusal": any other 1008 that names a reason, e.g. epoch conflict or missing
 *   cursor. The server said no and would say it again; fails closed without retry.
 * - "service_restart": the server is going away on purpose and expects to come back --
 *   1012 (service restart) or 1013 (try again later). Recoverable, but on a much longer
 *   clock than an "abnormal" drop: see the restart reconnect lane in `stream-session.ts`.
 * - "abnormal": unexpected drop, timeout (e.g. 1011 keepalive ping timeout), or transport error while recording is active.
 */
export type CloseKind =
  | "clean"
  | "resumable"
  | "terminal_refusal"
  | "service_restart"
  | "abnormal";

/** Whether the gateway closed with a reason it keeps the resume cursor for (#410). */
export function isResumableCloseReason(reason: string | null | undefined): boolean {
  return typeof reason === "string" && K.STREAM_RESUMABLE_CLOSE_REASONS.includes(reason);
}

export function classifyClose(diagnostics?: AudioUplinkSocketDiagnostics | null): CloseKind {
  if (!diagnostics) return "abnormal";
  if (diagnostics.wasClean && (diagnostics.closeCode === 1000 || diagnostics.closeCode === 1001)) {
    return "clean";
  }
  if (diagnostics.closeCode === 1008) {
    // [V] Every 1008 was a refusal here, but the gateway sends 1008 for its per-connection
    // cap too (`stream_byte_limit` at 15 min) and keeps the cursor so the device can resume.
    // Read as a refusal, it ended every device's upload for the rest of the meeting (#410).
    // The reason decides, from the one list the gateway also uses.
    if (isResumableCloseReason(diagnostics.closeReason)) return "resumable";
    // A 1008 with no reason is not the gateway's own refusal -- every one it sends names
    // its reason -- so it is treated as a drop and retried, not as a "no".
    if (!diagnostics.closeReason) return "abnormal";
    return "terminal_refusal";
  }
  // [V] 1012 is what a Railway redeploy looks like from the browser (#264): the platform
  // replaces the container with no drain budget, so every in-flight stream of every
  // device is cut within about a second of the others. 1013 is the same shape said
  // politely -- "try again later" -- and both mean *the server will be back*, which the
  // 1-then-4-second abnormal backoff is far too impatient to wait for.
  //
  // `wasClean` is deliberately not consulted here, and both shapes reach this arm. A
  // real uvicorn shutdown sends a proper close frame, so the 1012 the product actually
  // receives has `wasClean === true`; a killed container gives an unclean one. The clean
  // arm above is code-gated to 1000/1001, so without this arm a 1012 falls through to
  // "abnormal" either way and burns the ordinary 2-attempt budget inside 5 seconds
  // against a server that is still booting. Both shapes are pinned by tests.
  if (diagnostics.closeCode === 1012 || diagnostics.closeCode === 1013) {
    return "service_restart";
  }
  return "abnormal";
}

/** Exact 16-field client health snapshot allow-list for stream_health telemetry. */
export interface AudioUplinkHealthSnapshot {
  [key: string]: string | number | boolean | null;
  mode: "auto" | "manual";
  attempt: number;
  recording_age_ms: number;
  transport_age_ms: number;
  available_sample_count: number;
  sent_sample_offset: number;
  lag_ms: number;
  frames_sent: number;
  bytes_sent: number;
  flush_count: number;
  last_flush_gap_ms: number | null;
  max_flush_gap_ms: number;
  buffered_amount: number;
  max_buffered_amount: number;
  visibility: string;
  online: boolean;
}

export interface AudioUplinkOptions {
  sessionId: string;
  capture: AudioUplinkCapture;
  /** Timeline identity pinned to the capture lifecycle, not one socket. */
  timelineEpoch?: string;
  /** Optional origin override for tests; production reads the current PWA origin. */
  origin?: string;
  mode?: "auto" | "manual";
  attempt?: number;
  recordingStartedAtMs?: number;
  /** Injectable for browser tests; production uses the same-origin WebSocket constructor. */
  createSocket?: (url: string) => UplinkSocket;
  /** How long `stop()` may keep sending a backlog; defaults to `STOP_DRAIN_DEADLINE_MS`. */
  stopDrainDeadlineMs?: number;
  /** Diagnostics receives protocol summaries, never the ticket or bearer credential. */
  onMessage?: (message: AudioUplinkMessage) => void;
  onError?: (error: Error, diagnostics?: AudioUplinkSocketDiagnostics) => void;
  onClosed?: (diagnostics?: AudioUplinkSocketDiagnostics) => void;
  onHealth?: (snapshot: AudioUplinkHealthSnapshot) => void;
}

function socketDiagnostics(
  socket: UplinkSocket,
  event: "error" | "close",
  closeEvent?: Event,
): AudioUplinkSocketDiagnostics {
  const close = closeEvent as Partial<CloseEvent> | undefined;
  const browserOnline = typeof navigator === "undefined" ? null : navigator.onLine;
  const visibilityState = typeof document === "undefined" ? null : document.visibilityState;
  const wasClean = typeof close?.wasClean === "boolean" ? close.wasClean : false;
  return {
    event,
    closeCode: typeof close?.code === "number" ? close.code : null,
    closeReason: typeof close?.reason === "string" ? close.reason.slice(0, 64) || null : null,
    readyState: socket.readyState,
    bufferedAmount: socket.bufferedAmount,
    browserOnline,
    visibilityState,
    wasClean,
  };
}

function browserSocket(url: string): UplinkSocket {
  return new WebSocket(url);
}

/**
 * The explicit debug gate for the deployed PWA. The server-side stream flag remains the
 * authority: when it is off, the ticket request returns 404 and no socket is opened.
 */
export function streamProbeDebugEnabled(search = globalThis.location?.search ?? ""): boolean {
  return import.meta.env.DEV || new URLSearchParams(search).get("stream-probe") === "1";
}

/**
 * Manual probe mode query toggle for paired A/B diagnostic experiments.
 * When enabled (?probe_manual=1 / ?probe_manual=true), auto-start at record start is suppressed.
 * Decoupled from DEV mode.
 */
export function streamProbeManualMode(search = globalThis.location?.search ?? ""): boolean {
  const value = new URLSearchParams(search).get("probe_manual");
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
}

/** Build a same-origin ws/wss URL without putting the bearer token in a URL. */
export function streamUplinkWsUrl(
  sessionId: string,
  ticket: string,
  origin = globalThis.location?.origin ?? "http://localhost"
): string {
  const url = new URL(origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `/v1/stream/${encodeURIComponent(sessionId)}`;
  url.search = "";
  url.searchParams.set("ticket", ticket);
  return url.toString();
}

/** A sample offset or count as the gateway's acks carry it. */
function isSampleIndex(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

/** Backward-compatible local name for the shared canonical encoder. */
export const float32ToPcm16 = canonicalFloat32ToPcm16;

/** Adapt an existing Recorder without starting/stopping or otherwise changing batch capture. */
export function recorderCapture(
  recorder: Pick<AudioUplinkCapture, "sampleCount" | "seconds" | "sliceSamples" | "slice">
): AudioUplinkCapture {
  return {
    get sampleCount() {
      return recorder.sampleCount;
    },
    get seconds() {
      return recorder.seconds;
    },
    sliceSamples: recorder.sliceSamples.bind(recorder),
    slice: recorder.slice.bind(recorder),
  };
}

export class AudioUplink {
  private readonly options: AudioUplinkOptions;
  private readonly createSocket: (url: string) => UplinkSocket;
  private socket: UplinkSocket | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private healthTimer: ReturnType<typeof setInterval> | null = null;
  private flushPromise: Promise<void> | null = null;
  private stopPromise: Promise<void> | null = null;
  /** Settles when the gateway's close arrived or was forced. See `closed()`. */
  private closedPromise: Promise<void> | null = null;
  private closeForced = false;
  private timelineEpoch: string | null = null;
  private transportEpoch: string | null = null;
  private sequence = 0;
  private sampleOffset = 0;
  private closing = false;
  private started = false;
  private generation = 0;
  private mode: "auto" | "manual" = "auto";
  private attempt = 1;
  private recordingStartedAtMs: number | null = null;
  private transportStartedAtMs: number | null = null;
  private framesSent = 0;
  private bytesSent = 0;
  private flushCount = 0;
  private lastFlushAtMs: number | null = null;
  private lastFlushGapMs: number | null = null;
  private maxFlushGapMs = 0;
  private maxBufferedAmount = 0;
  private terminalHealthEmitted = false;
  /**
   * The resume probe (#356). Every connection sends exactly one frame from sample 0 and
   * waits for its ack before sending more. A `consumed` ack carries the server's durable
   * cursor, and the uplink jumps straight to it rather than replaying the whole capture;
   * an `accepted` ack means there is no durable cursor and sending simply continues.
   *
   * Always from (0, 0), never from where this device last got to: the durable anchor is a
   * completed-hop boundary at or below what was sent, and a first frame *past* it is a
   * `resume_cursor_required` refusal (1008, terminal).
   */
  private probe: "pending" | "awaiting_ack" | "done" = "pending";
  /** Every captured sample was sent and the socket buffer emptied before close. */
  private backlogComplete = false;
  /**
   * The gateway closed this connection with a resumable reason (#410). Sent is then not
   * the same as received: frames past the cap were dropped, so the backlog is not done.
   */
  private serverAskedToResume = false;
  /**
   * The end of the audio the server has acknowledged on this connection, frame by frame
   * and contiguously (#894). Moved like the core's `note_ack`: the probe's ack states
   * where the server is, and after it only an `accepted` ack that continues the run counts,
   * so a duplicate, a gap or an ack for a frame not yet sent cannot mark audio delivered.
   */
  private ackedSampleOffset = 0;
  /**
   * What this connection had sent when its stop drain completed: the capture it owes. The
   * recorder can still grow after that -- `Recorder.stop()` appends the resampler's tail
   * after the stream has stopped -- and the live count would then never be acked.
   */
  private drainedSampleOffset: number | null = null;
  /** Woken once the server has acknowledged every captured sample. See `whenDelivered`. */
  private deliveredWaiters: Array<() => void> = [];

  constructor(options: AudioUplinkOptions) {
    this.options = options;
    this.createSocket = options.createSocket ?? browserSocket;
  }

  async start(): Promise<void> {
    if (this.started) throw new Error("stream probe already started");
    const generation = ++this.generation;
    this.closing = false;
    this.stopPromise = null;
    this.closedPromise = null;
    this.closeForced = false;
    this.timelineEpoch = null;
    this.transportEpoch = null;
    this.sequence = 0;
    this.sampleOffset = 0;
    this.started = true;
    this.mode = this.options.mode ?? "auto";
    this.attempt = this.options.attempt ?? 1;
    this.recordingStartedAtMs = this.options.recordingStartedAtMs ?? performance.now();
    this.transportStartedAtMs = performance.now();
    this.framesSent = 0;
    this.bytesSent = 0;
    this.flushCount = 0;
    this.lastFlushAtMs = null;
    this.lastFlushGapMs = null;
    this.maxFlushGapMs = 0;
    this.maxBufferedAmount = 0;
    this.terminalHealthEmitted = false;
    this.probe = "pending";
    this.backlogComplete = false;
    this.serverAskedToResume = false;
    this.ackedSampleOffset = 0;
    this.drainedSampleOffset = null;

    this.clearHealthTimer();
    this.healthTimer = setInterval(() => {
      this.emitHealth();
    }, 5_000);

    try {
      // `issueStreamTicket` uses the existing in-memory API auth closure. This module never
      // receives, reads, stores, logs, or adds a bearer token to the WebSocket URL.
      const response = await api.issueStreamTicket(this.options.sessionId);
      this.ensureCurrent(generation);
      const ticket = response?.ticket;
      if (typeof ticket !== "string" || ticket.length === 0) {
        throw new Error("stream ticket response was missing a ticket");
      }

      const randomEpoch = () =>
        globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
      this.timelineEpoch = this.options.timelineEpoch ?? randomEpoch();
      this.transportEpoch = randomEpoch();
      const origin = this.options.origin ?? globalThis.location?.origin ?? "http://localhost";
      const socket = this.createSocket(streamUplinkWsUrl(this.options.sessionId, ticket, origin));
      this.ensureCurrent(generation);
      this.socket = socket;
      socket.binaryType = "arraybuffer";
      socket.onmessage = (event) => {
        // stop() advances the generation to cancel pending work, but the active socket
        // still needs to receive the gateway's close acknowledgement.
        if (this.generation === generation || (this.closing && this.socket === socket)) {
          this.receive(event.data);
        }
      };
      socket.onerror = (event) => {
        if (this.generation === generation) {
          this.reportError(
            new Error("stream probe socket error"),
            socketDiagnostics(socket, "error", event),
          );
        }
      };
      socket.onclose = (event) => {
        if (this.generation !== generation && !(this.closing && this.socket === socket)) return;
        const diagnostics = socketDiagnostics(socket, "close", event);
        if (classifyClose(diagnostics) === "resumable") this.serverAskedToResume = true;
        this.emitTerminalHealth();
        this.socket = null;
        this.clearTimer();
        this.started = false;
        if (!this.closing) {
          this.reportError(new Error("stream probe socket closed"), diagnostics);
        }
        this.options.onClosed?.(diagnostics);
      };
      await this.waitForOpen(socket);
      this.ensureCurrent(generation);
      // A fresh open sends no `resume`. The gateway remembers the acknowledged
      // cursor across an unclean drop, so asserting a synthetic (0, 0) here
      // would close the very reconnect seam the resume contract exists for
      // (`resume_cursor_not_contiguous`). `capture_origin_local_sec` belongs on
      // a genuine reconnect carrying the real cursor; the gateway already
      // defaults it to 0 when absent.
      this.timer = setInterval(() => {
        void this.flush().catch((error: unknown) => this.reportError(asError(error)));
      }, 100);
      await this.flush();
    } catch (error) {
      await this.abortSocket();
      if (this.generation === generation) this.started = false;
      throw error;
    }
  }

  /**
   * Send every captured sample and ask the gateway to close. Does not wait.
   *
   * [V] #356: the first flush is still awaited here, but a backlog that does not fit in
   * one flush -- the socket buffer is bounded at `MAX_SOCKET_BUFFERED_BYTES`, about 33 s
   * of audio -- is no longer cut off. The close frame goes only once the whole capture
   * is sent and the buffer is empty, or `stopDrainDeadlineMs` runs out. That drain runs
   * detached, before the close wait below, so `closed()` covers both.
   *
   * **This used to await the gateway's close event, and that wait was 3 s of the
   * stop path.** [M] On ses_8333 the creator spent 2.36 s here and the guest hit
   * the full `CLOSE_WAIT_MS` timeout, and *all* of it was ahead of final scoring
   * and the watermark POST -- so a stage that produces nothing the rest of the
   * chain reads was serialised in front of two that do. The gateway closes only
   * after provider termination (+1.2 s) and archive drain (+2.1-2.4 s); the
   * client has no use for that news.
   *
   * The wait still happens, in `closed()`, and it still force-closes at
   * `CLOSE_WAIT_MS` -- but off the critical path. Nothing about what the gateway
   * does changed: the provider terminate and both drains start on the
   * `{"type":"close"}` frame this method sends, exactly as before. What changed
   * is that the client no longer blocks on their *result*. The server-side stop
   * barrier (#172) is what proves those drains happened, and it always was --
   * the client never proved it.
   */
  async stop(): Promise<void> {
    if (!this.started && !this.socket) return;
    this.generation += 1;
    if (this.stopPromise) return this.stopPromise;
    this.closing = true;
    this.stopPromise = this.stopInternal();
    await this.stopPromise;
  }

  /**
   * Resolve once the gateway's close arrived, or `CLOSE_WAIT_MS` forced it.
   *
   * Safe to await zero times or many: `stop()` starts the timer, so the
   * force-close happens whether or not anyone is listening. **Never rejects.**
   * A caller awaiting this is trying to tear down cleanly, and a rejection
   * there would turn a socket that failed to close politely into an unhandled
   * error on the stop path.
   */
  async closed(): Promise<void> {
    if (this.closedPromise) return this.closedPromise;
    return Promise.resolve();
  }

  /** Whether `CLOSE_WAIT_MS` expired and this client closed the socket itself. */
  closeWasForced(): boolean {
    return this.closeForced;
  }

  /**
   * Whether the stop drain sent the whole capture before it closed.
   *
   * Only meaningful once `closed()` has settled. `false` means some of this recording's
   * audio never left the device on this connection -- the socket dropped during the drain,
   * or the deadline ran out -- and the caller may open another connection to finish it.
   */
  backlogWasDrained(): boolean {
    return this.backlogComplete;
  }

  /**
   * Whether the server now has every sample of the capture, for a caller that has stopped
   * this uplink (#894).
   *
   * Resolves `true` as soon as the server has acknowledged every captured sample, which on
   * an ordinary stop is one round trip after the last frame -- well before the gateway's
   * close, so a caller holding its report on this does not pay `CLOSE_WAIT_MS` (#272 L1).
   * Otherwise it waits for `closed()` and answers what the connection achieved: all acked,
   * or the whole capture sent before a clean close (`backlogWasDrained`). Never rejects.
   */
  async whenDelivered(): Promise<boolean> {
    if (this.allAcked()) return true;
    await Promise.race([
      new Promise<void>((resolve) => this.deliveredWaiters.push(resolve)),
      this.closed(),
    ]);
    return this.allAcked() || this.backlogComplete;
  }

  private allAcked(): boolean {
    const owed =
      this.drainedSampleOffset ?? Math.max(0, Math.floor(this.options.capture.sampleCount));
    return this.ackedSampleOffset >= owed;
  }

  private markDrained(): void {
    this.backlogComplete = true;
    this.drainedSampleOffset = this.sampleOffset;
    // The last ack can land before the drain notices it is done.
    this.wakeIfDelivered();
  }

  private wakeIfDelivered(): void {
    if (this.deliveredWaiters.length === 0 || !this.allAcked()) return;
    const waiters = this.deliveredWaiters;
    this.deliveredWaiters = [];
    for (const wake of waiters) wake();
  }

  /** Move the acknowledged cursor on one ack from this connection. See `ackedSampleOffset`. */
  private noteAck(message: AudioUplinkMessage): void {
    if (message.type !== "ack") return;
    if (message.timeline_epoch !== this.timelineEpoch) return;
    if (message.transport_epoch !== this.transportEpoch) return;
    if (message.status === "consumed") {
      // Only the probe's: it carries the server's durable cursor for this connection.
      if (this.probe !== "awaiting_ack") return;
      const next = message.next_sample_offset;
      if (!isSampleIndex(next)) return;
      this.ackedSampleOffset = Math.min(next, Math.floor(this.options.capture.sampleCount));
    } else if (message.status === "accepted") {
      const start = message.sample_offset;
      const count = message.sample_count;
      if (!isSampleIndex(start) || !isSampleIndex(count)) return;
      const end = start + count;
      if (end > this.sampleOffset) return;
      if (this.probe === "awaiting_ack") {
        if (start === 0) this.ackedSampleOffset = end;
      } else if (start <= this.ackedSampleOffset && end > this.ackedSampleOffset) {
        this.ackedSampleOffset = end;
      }
    } else {
      return;
    }
    this.wakeIfDelivered();
  }

  /** The sample the next frame would start at; for callers reporting what is still owed. */
  get sentSampleOffset(): number {
    return this.sampleOffset;
  }

  private async stopInternal(): Promise<void> {
    this.closing = true;
    this.clearTimer();
    const generation = this.generation;
    const socket = this.socket;
    try {
      await this.flush();
    } catch (error) {
      this.reportError(asError(error));
    }
    // `started` clears now, not at settlement. It means "this uplink is
    // capturing", and once the samples are flushed and the close frame is away
    // it is not -- the socket draining afterwards is the gateway's business.
    // Clearing it here is also what keeps `stop()` then `start()` working: a
    // gateway that never closes would otherwise hold `started` true for the
    // whole of `CLOSE_WAIT_MS` and make the next recording throw
    // "already started".
    this.started = false;
    // The close wait is started here rather than awaited: the timer that
    // guarantees a force-close is running, and `closed()` is now resolvable by
    // anyone who wants it. Terminal health waits for the settlement because it
    // is a snapshot *of the closed connection*, and `socket` waits with it so
    // that a server `close` message arriving in the meantime can still be acted
    // on by `receive`.
    //
    // **Generation-guarded, and it has to be.** Because this settlement is now
    // deferred, a `start()` for the *next* recording can land while the old
    // socket is still draining -- and an ungated `this.socket = null` there
    // would pull the new run's socket out from under it. Same guard, and the
    // same reason, as `start()`'s own error path.
    //
    // The close frame goes now, synchronously, when the flush above already sent
    // everything -- the ordinary stop -- and only after the detached drain otherwise.
    const drained = socket !== null && this.isDrained(socket);
    if (drained) this.markDrained();
    const closing = drained
      ? this.closeSocket(socket)
      : this.drainForStop(socket).then(() => this.closeSocket(socket));
    this.closedPromise = closing
      .catch(() => undefined)
      .finally(() => {
        this.emitTerminalHealth();
        // Everything left the device, but the gateway closed at its cap (or for another
        // resumable reason) before taking all of it: the tail is still owed (#410).
        if (this.serverAskedToResume) this.backlogComplete = false;
        if (this.generation !== generation) return;
        this.socket = null;
      });
  }

  /**
   * Keep sending until the whole capture is out and the socket buffer is empty.
   *
   * Bounded by `stopDrainDeadlineMs`, and it ends early if the socket stops being open:
   * a drop mid-drain is the caller's to recover, which `backlogWasDrained()` tells it.
   * A probe still waiting for its ack is waited for like any other backlog.
   */
  private async drainForStop(socket: UplinkSocket | null): Promise<void> {
    if (!socket) return;
    const deadline = performance.now() + (this.options.stopDrainDeadlineMs ?? STOP_DRAIN_DEADLINE_MS);
    for (;;) {
      if (socket.readyState !== SOCKET_OPEN || this.socket !== socket) return;
      try {
        await this.flush();
      } catch {
        return;
      }
      if (this.isDrained(socket)) {
        this.markDrained();
        return;
      }
      if (performance.now() >= deadline) return;
      await new Promise<void>((resolve) => setTimeout(resolve, STOP_DRAIN_POLL_MS));
    }
  }

  private isDrained(socket: UplinkSocket): boolean {
    const available = Math.max(0, Math.floor(this.options.capture.sampleCount));
    return this.probe === "done" && this.sampleOffset >= available && socket.bufferedAmount === 0;
  }

  private async waitForOpen(socket: UplinkSocket): Promise<void> {
    if (socket.readyState === SOCKET_OPEN) return;
    await new Promise<void>((resolve, reject) => {
      const priorOpen = socket.onopen;
      const priorError = socket.onerror;
      const priorClose = socket.onclose;
      socket.onopen = (event) => {
        priorOpen?.(event);
        resolve();
      };
      socket.onerror = (event) => {
        priorError?.(event);
        reject(new Error("stream probe socket failed to open"));
      };
      socket.onclose = (event) => {
        priorClose?.(event);
        reject(new Error("stream probe socket closed before opening"));
      };
    });
  }

  private async flush(): Promise<void> {
    if (this.flushPromise) return this.flushPromise;
    this.flushPromise = this.flushInternal().finally(() => {
      this.flushPromise = null;
    });
    return this.flushPromise;
  }

  private async flushInternal(): Promise<void> {
    const socket = this.socket;
    if (!socket || socket.readyState !== SOCKET_OPEN) return;
    const now = performance.now();
    this.flushCount += 1;
    if (this.lastFlushAtMs !== null) {
      const gap = Math.max(0, Math.round(now - this.lastFlushAtMs));
      this.lastFlushGapMs = gap;
      if (gap > this.maxFlushGapMs) {
        this.maxFlushGapMs = gap;
      }
    }
    this.lastFlushAtMs = now;

    if (socket.bufferedAmount > this.maxBufferedAmount) {
      this.maxBufferedAmount = socket.bufferedAmount;
    }
    if (socket.bufferedAmount > MAX_SOCKET_BUFFERED_BYTES) return;
    const timelineEpoch = this.timelineEpoch;
    const transportEpoch = this.transportEpoch;
    if (!timelineEpoch || !transportEpoch) return;

    // One probe frame, then nothing until its ack says where to continue from.
    if (this.probe === "awaiting_ack") return;
    const available = Math.max(0, Math.floor(this.options.capture.sampleCount));
    while (this.sampleOffset < available && socket.readyState === SOCKET_OPEN) {
      if (socket.bufferedAmount > this.maxBufferedAmount) {
        this.maxBufferedAmount = socket.bufferedAmount;
      }
      if (socket.bufferedAmount > MAX_SOCKET_BUFFERED_BYTES) return;
      const remaining = available - this.sampleOffset;
      const requested = Math.min(STREAM_FRAME_SAMPLES, remaining);
      const samples = this.options.capture.sliceSamples(
        this.sampleOffset,
        this.sampleOffset + requested
      );
      if (samples.length === 0) return;
      const frameSamples = samples.length > requested ? samples.subarray(0, requested) : samples;
      const payload = float32ToPcm16(frameSamples);
      const metadata: StreamFrameMetadata = {
        type: "audio",
        timeline_epoch: timelineEpoch,
        transport_epoch: transportEpoch,
        sequence: this.sequence,
        sample_offset: this.sampleOffset,
        sample_count: frameSamples.length,
        sample_rate_hz: K.SAMPLE_RATE,
        kind: "audio",
        byte_length: payload.byteLength,
      };
      socket.send(JSON.stringify(metadata));
      socket.send(payload);
      this.sequence += 1;
      this.sampleOffset += frameSamples.length;
      this.framesSent += 1;
      this.bytesSent += payload.byteLength;
      if (this.probe === "pending") {
        this.probe = "awaiting_ack";
        return;
      }
    }
  }

  /**
   * Settle the resume probe from its ack, and continue sending.
   *
   * Guarded on both epochs: an ack for another timeline or another transport cannot
   * move this connection's cursor. Both `next_*` values are taken as given rather than
   * one derived from the other, because frames are not all the same size.
   */
  private settleProbe(message: AudioUplinkMessage): void {
    if (this.probe !== "awaiting_ack" || message.type !== "ack") return;
    if (message.timeline_epoch !== this.timelineEpoch) return;
    if (message.transport_epoch !== this.transportEpoch) return;
    if (message.status === "consumed") {
      const nextSequence = message.next_sequence;
      const nextSampleOffset = message.next_sample_offset;
      if (
        typeof nextSequence !== "number" ||
        typeof nextSampleOffset !== "number" ||
        !Number.isInteger(nextSequence) ||
        !Number.isInteger(nextSampleOffset) ||
        nextSequence < 0 ||
        nextSampleOffset < 0
      ) {
        return;
      }
      this.sequence = nextSequence;
      this.sampleOffset = nextSampleOffset;
    } else if (message.status !== "accepted") {
      return;
    }
    this.probe = "done";
    // Chained behind a flush still settling, so the continuation is not swallowed by
    // `flush()` returning the in-flight promise.
    const pending = this.flushPromise ?? Promise.resolve();
    void pending
      .then(() => this.flush())
      .catch((error: unknown) => this.reportError(asError(error)));
  }

  private receive(data: unknown): void {
    if (typeof data !== "string") return;
    try {
      const message = JSON.parse(data) as AudioUplinkMessage;
      if (message && typeof message === "object") {
        // Before the probe settles: the probe's own ack is read against the probe state.
        this.noteAck(message);
        this.settleProbe(message);
        if (message.type === "close" && isResumableCloseReason(message.reason)) {
          this.serverAskedToResume = true;
        }
        this.options.onMessage?.(message);
        if (this.closing && message.type === "close") {
          this.socket?.close(1000, message.reason ?? "server_close");
        }
      }
    } catch {
      // The gateway's control messages are JSON; malformed provider output is not a
      // credential-bearing client concern and is intentionally ignored by this diagnostics UI.
    }
  }

  private async closeSocket(target?: UplinkSocket | null): Promise<void> {
    // The socket is passed in by `stopInternal` rather than read from `this`,
    // because this now outlives the `stop()` that started it: reading
    // `this.socket` here could close a *later* run's socket.
    const socket = target === undefined ? this.socket : target;
    if (!socket || socket.readyState === SOCKET_CLOSED) return;
    if (socket.readyState === SOCKET_CLOSING) return;

    await new Promise<void>((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const timeout = setTimeout(() => {
        try {
          this.closeForced = true;
          socket.close(1000, "client_stop");
        } finally {
          settle();
        }
      }, CLOSE_WAIT_MS);
      const closeAndClear = () => {
        clearTimeout(timeout);
        settle();
      };
      const priorClose = socket.onclose;
      socket.onclose = (event) => {
        priorClose?.(event);
        closeAndClear();
      };
      try {
        socket.send(JSON.stringify({ type: "close" }));
      } catch {
        clearTimeout(timeout);
        socket.close(1000, "client_stop");
        closeAndClear();
      }
    });
  }

  private async abortSocket(): Promise<void> {
    this.clearTimer();
    this.emitTerminalHealth();
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState !== SOCKET_CLOSED) {
      try {
        socket.close(1000, "client_abort");
      } catch {
        // A failed handshake is already being reported by the caller.
      }
    }
  }

  private reportError(error: Error, diagnostics?: AudioUplinkSocketDiagnostics): void {
    if (!this.closing) this.options.onError?.(error, diagnostics);
  }

  private clearTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  private clearHealthTimer(): void {
    if (this.healthTimer) clearInterval(this.healthTimer);
    this.healthTimer = null;
  }

  private emitHealth(): AudioUplinkHealthSnapshot {
    const now = performance.now();
    const transportAgeMs =
      this.transportStartedAtMs !== null
        ? Math.max(0, Math.round(now - this.transportStartedAtMs))
        : 0;
    const recordingAgeMs =
      this.recordingStartedAtMs !== null
        ? Math.max(0, Math.round(now - this.recordingStartedAtMs))
        : transportAgeMs;
    const availableSampleCount = Math.max(0, Math.floor(this.options.capture.sampleCount));
    const sentSampleOffset = this.sampleOffset;
    const lagMs = Math.max(
      0,
      Math.round(((availableSampleCount - sentSampleOffset) / K.SAMPLE_RATE) * 1000)
    );
    const currentBuffered = this.socket?.bufferedAmount ?? 0;
    if (currentBuffered > this.maxBufferedAmount) {
      this.maxBufferedAmount = currentBuffered;
    }
    const visibility =
      typeof document === "undefined" || !document.visibilityState
        ? "unknown"
        : document.visibilityState;
    const online =
      typeof navigator === "undefined" || typeof navigator.onLine !== "boolean"
        ? true
        : navigator.onLine;

    const snapshot: AudioUplinkHealthSnapshot = {
      mode: this.mode,
      attempt: this.attempt,
      recording_age_ms: recordingAgeMs,
      transport_age_ms: transportAgeMs,
      available_sample_count: availableSampleCount,
      sent_sample_offset: sentSampleOffset,
      lag_ms: lagMs,
      frames_sent: this.framesSent,
      bytes_sent: this.bytesSent,
      flush_count: this.flushCount,
      last_flush_gap_ms: this.lastFlushGapMs,
      max_flush_gap_ms: this.maxFlushGapMs,
      buffered_amount: currentBuffered,
      max_buffered_amount: this.maxBufferedAmount,
      visibility,
      online,
    };
    this.options.onHealth?.(snapshot);
    return snapshot;
  }

  private emitTerminalHealth(): void {
    if (this.terminalHealthEmitted) return;
    this.terminalHealthEmitted = true;
    this.clearHealthTimer();
    this.emitHealth();
  }

  private ensureCurrent(generation: number): void {
    if (this.generation !== generation || this.closing) {
      throw new Error("stream probe cancelled");
    }
  }
}

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}
